import { useEffect, useState } from 'react';
import { GlassCard } from '@/components/GlassCard';
import { Icon, type IconName } from '@/components/Icon';
import { Reveal } from '@/components/Reveal';
import { api, ApiError } from '@/lib/api';

interface Stats {
  totalUsers: number;
  signupsLast24h: number;
  dailyActiveUsers: number;
  paidUsers: number;
  aiCallsToday: number;
  activeSessions: number;
}

export function AdminOverview(): JSX.Element {
  const [stats, setStats] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<Stats>('/api/admin/stats')
      .then(setStats)
      .catch((e) => setError(e instanceof ApiError ? e.message : '加载失败'));
  }, []);

  if (error !== null) {
    return <div className="rounded-2xl border border-ember-400/30 bg-ember-400/[0.06] p-4 text-sm text-ember-200">⚠ {error}</div>;
  }
  if (stats === null) {
    return <div className="text-cream-400 text-sm font-arcade tracking-widest">LOADING…</div>;
  }

  const cards: Array<{ label: string; value: number; tone: 'cyan' | 'lime' | 'pink' | 'cream'; icon: IconName }> = [
    { label: '总用户',       value: stats.totalUsers,       tone: 'cyan',  icon: 'profile' },
    { label: '24h 新增',     value: stats.signupsLast24h,   tone: 'lime',  icon: 'vocab' },
    { label: '24h 活跃',     value: stats.dailyActiveUsers, tone: 'cyan',  icon: 'streak' },
    { label: 'VIP 用户',     value: stats.paidUsers,        tone: 'pink',  icon: 'pro' },
    { label: '今日 AI 调用', value: stats.aiCallsToday,     tone: 'cream', icon: 'ai' },
    { label: '活跃 session', value: stats.activeSessions,   tone: 'cream', icon: 'shield' as IconName },
  ];

  const TONE_COLOR: Record<typeof cards[number]['tone'], string> = {
    cyan: 'text-honey-300',
    lime: 'text-mint-300',
    pink: 'text-ember-300',
    cream: 'text-cream-200',
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        {cards.map((c, i) => (
          <Reveal key={c.label} delay={i * 0.04}>
            <GlassCard padding="p-5" dotmatrix={false}>
              <div className="flex items-center justify-between">
                <Icon name={c.icon === ('shield' as IconName) ? 'admin' : c.icon} size="lg" className={TONE_COLOR[c.tone]} />
                <span className={`font-score text-4xl ${TONE_COLOR[c.tone]} text-neon`}>
                  {c.value.toLocaleString()}
                </span>
              </div>
              <div className="mt-3 text-xs text-cream-400 font-arcade tracking-widest">{c.label}</div>
            </GlassCard>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <GlassCard padding="p-6" dotmatrix={false}>
          <div className="font-arcade text-xs tracking-widest text-honey-300 mb-3">QUICK NOTES</div>
          <ul className="text-sm text-cream-300/90 space-y-2">
            <li>· 第一个登录的邮箱 = 自动 admin（系统首启 bootstrap）</li>
            <li>· Magic code 在 EXPOSE_MAGIC_CODE=true 时直接返回前端，省去发邮件</li>
            <li>· session 30 天有效；DB 没自动清理，按需手动 cron</li>
            <li>· AI 配额表 (ai_usage) 已就位，但当前 TapToAsk 仍在用 localStorage — 下一步迁移</li>
          </ul>
        </GlassCard>
      </Reveal>
    </div>
  );
}
